import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSupabaseClient } from '../lib/supabase';

interface ClientCapacityCareProps {
  onComplete?: () => void;
}

export default function ClientCapacityCare({ onComplete }: ClientCapacityCareProps) {
  const supabase = getSupabaseClient();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [consultationsPerWeek, setConsultationsPerWeek] = useState('');
  const [newClientsPerMonth, setNewClientsPerMonth] = useState('');
  const [platformCommitment, setPlatformCommitment] = useState('');
  const [serviceFeeAcceptance, setServiceFeeAcceptance] = useState('');

  // Scroll to top when navigating between questions
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  // Load existing data on component mount
  useEffect(() => {
    const loadExistingData = async () => {
      try {
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        if (authError || !user) {
          console.error('Auth error:', authError);
          setLoading(false);
          return;
        }

        // Try to load existing capacity data
        const { data: existingData, error } = await supabase
          .from('guide_client_capacity')
          .select('*')
          .eq('guide_id', user.id)
          .single();

        if (error && error.code !== 'PGRST116') { // PGRST116 is "not found"
          console.error('Error loading client capacity data:', error);
        } else if (existingData) {
          // Populate form with existing data
          setConsultationsPerWeek(existingData.consultations_per_week || '');
          setNewClientsPerMonth(existingData.new_clients_per_month || '');
          setPlatformCommitment(existingData.platform_commitment || '');
          setServiceFeeAcceptance(existingData.service_fee_acceptance || '');
        }
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadExistingData();
  }, []);

  const steps = [
    {
      id: 'consultations',
      stepLabel: 'Client Capacity & Care – Step 1 of 4',
      question: 'How many consultation calls are you willing to take per week?',
      greenBox: {
        heading: 'Guides Collective brings a steady stream of qualified seekers straight to you.',
        body: 'Every guide on the platform is asked to take free, 20-minute consultation calls with seekers who have already completed our intake and been matched to you based on specialty, personality fit, and experience level. Your answer helps us match you only with the number of seekers you\'re comfortable supporting.',
        listHeading: 'These consultations help you:',
        listItems: [
          'Build trust',
          'Determine fit',
          'Understand their goals',
          'Enroll aligned clients into your programs'
        ]
      },
      options: [
        '0–2 consultations per week',
        '3–5 consultations per week',
        '6–10 consultations per week',
        '10–15 consultations per week',
        '15+ consultations per week'
      ],
      value: consultationsPerWeek,
      setValue: setConsultationsPerWeek,
    },
    {
      id: 'clients',
      stepLabel: 'Client Capacity & Care – Step 2 of 4',
      question: 'How many new clients do you feel comfortable taking on each month?',
      options: [
        '0–1 new clients per month',
        '2–3 new clients per month',
        '4–6 new clients per month',
        '7–10 new clients per month',
        '10+ new clients per month'
      ],
      value: newClientsPerMonth,
      setValue: setNewClientsPerMonth,
      whyWeAsk: {
        title: 'Why we ask this',
        items: [
          'You are matched only with the number of seekers you can fully support',
          'Your caseload remains healthy and sustainable',
          'You never feel overwhelmed by incoming clients',
          'Your practice grows in a way that aligns with your wellbeing'
        ]
      },
    },
    {
      id: 'platform',
      stepLabel: 'Client Capacity & Care – Step 3 of 4',
      question: 'Do you agree to use Guides Collective for all client communication, scheduling, and payments for seekers matched through the platform?',
      featureCards: [
        {
          title: 'Private Messaging',
          items: ['End to end encrypted messaging', 'Private video calls', 'Scheduling & session reminders']
        },
        {
          title: 'HIPAA Compliance',
          items: ['Payments & invoicing', 'Contract renewals', 'Client records']
        },
        {
          title: 'Secure Management',
          items: ['Ongoing support and re-engagement', 'Review Management', 'Matching and client acquisition']
        }
      ],
      greenBox: {
        heading: 'Moving Seekers Off-Platform',
        listItems: [
          'Breaks our safety standards',
          'Undermines our matching system',
          'Violates platform integrity',
          'Will result in removal from Guides Collective'
        ],
        footer: 'We are committed to giving you a steady flow of aligned clients, and we ask for your commitment to using the platform as it\'s intended.'
      },
      options: [
        'Yes, I agree and understand the expectations',
        'No, I\'m not able to commit to this requirement'
      ],
      value: platformCommitment,
      setValue: setPlatformCommitment,
    },
    {
      id: 'fee',
      stepLabel: 'Client Capacity & Care – Step 4 of 4',
      question: 'Do you acknowledge and accept the 15% service fee for client contracts?',
      featureCards: [
        {
          title: 'You Keep 85% of your earnings',
          description: 'You are welcome to increase your rates by 15%. Many guides simply add the fee on top of their standard pricing.'
        },
        {
          title: 'Pricing & Volume',
          description: 'Keep in mind your pricing does influence how many seekers you\'re matched with. Lower or mid-range pricing may increase your match volume.'
        },
        {
          title: 'Your Value',
          description: 'We encourage you to choose pricing that reflects the value of your work, your financial goals, and the type of clients you want to attract.'
        }
      ],
      options: [
        'Yes, I understand and accept the 15% fee',
        'I need more clarification before agreeing'
      ],
      value: serviceFeeAcceptance,
      setValue: setServiceFeeAcceptance,
    }
  ];

  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;

  const canProceedToNext = () => {
    return !!currentStepData.value;
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setError(null);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);

    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        setError('Authentication error. Please log in again.');
        return;
      }

      // Prepare the data
      const capacityData = {
        guide_id: user.id,
        consultations_per_week: consultationsPerWeek,
        new_clients_per_month: newClientsPerMonth,
        platform_commitment: platformCommitment,
        service_fee_acceptance: serviceFeeAcceptance,
        updated_at: new Date().toISOString()
      };

      // Upsert the capacity data
      const { error: upsertError } = await supabase
        .from('guide_client_capacity')
        .upsert(capacityData, {
          onConflict: 'guide_id'
        });

      if (upsertError) {
        console.error('Error saving client capacity data:', upsertError);
        throw new Error(upsertError.message || 'Failed to save client capacity data');
      }

      // Trigger the refresh function instead of navigating
      // Don't set saving to false here - let navigation handle it
      if (onComplete) {
        onComplete();
      } else {
        navigate('/onboarding');
      }
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      setError(error instanceof Error ? error.message : 'An unexpected error occurred. Please try again.');
      setSaving(false); // Only reset on error
    }
  };

  const handleNext = async () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      setError(null);
    } else {
      await handleSubmit();
    }
  };

  const renderQuestion = () => {
    const step = currentStepData;

    return (
      <div className="w-full">
        {/* Question as heading */}
        <h1 className="mb-10 text-[42px] leading-[44px] font-medium text-[#304031] font-recoleta">
          {step.question}
        </h1>

        {/* Feature cards (for platform step) */}
        {(step as any).featureCards && (
          <div className="mb-8 grid grid-cols-1 md:grid-cols-3 gap-8">
            {(step as any).featureCards.map((card: { title: string; items?: string[]; description?: string }, idx: number) => (
              <div key={idx} className="flex flex-col gap-2">
                <img src="/icon.svg" alt="" className="w-6 h-6 mb-2" />
                <h3 className="text-2xl font-medium text-[#214D3C] font-recoleta leading-[1.6]">{card.title}</h3>
                {card.items && (
                  <ul className="space-y-2 text-base text-[#214D3C]">
                    {card.items.map((item: string, itemIdx: number) => (
                      <li key={itemIdx} className="flex items-start gap-2">
                        <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#304031] flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
                {card.description && (
                  <p className="text-base text-[#214D3C] leading-[1.2]">{card.description}</p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Green box content */}
        {(step as any).greenBox && (
          <div className="mb-8 rounded-2xl bg-[#214D3C] p-6 text-[#FFFCEF]">
            <img src="/icon.svg" alt="" className="w-6 h-6 mb-4 [filter:brightness(0)_invert(98%)_sepia(6%)_saturate(365%)_hue-rotate(338deg)_brightness(103%)]" />
            <h3 className="text-lg font-bold mb-3">{(step as any).greenBox.heading}</h3>
            {(step as any).greenBox.body && (
              <p className="text-sm leading-[1.4] mb-4">{(step as any).greenBox.body}</p>
            )}
            {(step as any).greenBox.listHeading && (
              <p className="text-sm mb-2">{(step as any).greenBox.listHeading}</p>
            )}
            {(step as any).greenBox.listItems && (
              <ul className="space-y-1 text-sm mb-4">
                {(step as any).greenBox.listItems.map((item: string, idx: number) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#FFFCEF] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            )}
            {(step as any).greenBox.footer && (
              <p className="text-sm leading-[1.4] text-[#FFFCEF]/80">{(step as any).greenBox.footer}</p>
            )}
          </div>
        )}

        {/* Supporting content between heading and options */}
        {(step.heading || step.body) && (
          <div className="mb-6">
            {step.heading && (
              <p className="mb-4 text-2xl font-semibold text-[#304031]">
                {step.heading}
              </p>
            )}
            {step.body}
          </div>
        )}

        {step.helperText && (
          <p className="mb-6 text-base text-[#304031]/70">{step.helperText}</p>
        )}

        {/* Question options */}
        {(step as any).options && (
          <fieldset className="flex flex-col gap-3 w-full mb-10">
            <legend className="sr-only">
              {step.question}
            </legend>
            {(step as any).options.map((opt: string, index: number) => {
              const selected = step.value === opt;
              const optionId = `${step.id}-${index}`;
              return (
                <label
                  key={opt}
                  htmlFor={optionId}
                  className={`flex items-start gap-3 rounded-xl border border-transparent px-4 py-3 text-base transition hover:bg-[#304031]/5 cursor-pointer ${
                    selected ? 'font-semibold text-[#2C2731]' : 'text-[#2C2731]'
                  }`}
                >
                  <input
                    id={optionId}
                    type="radio"
                    name={step.id}
                    checked={selected}
                    onChange={() => step.setValue(opt)}
                    className="sr-only"
                  />
                  <span
                    aria-hidden
                    className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition ${
                      selected ? 'border-[#304031]' : 'border-[#304031]/40'
                    }`}
                  >
                    <span
                      className={`h-2.5 w-2.5 rounded-full transition ${
                        selected ? 'bg-[#304031]' : 'bg-transparent'
                      }`}
                    />
                  </span>
                  <span className="text-base text-[#2C2731]">{opt}</span>
                </label>
              );
            })}
          </fieldset>
        )}

        {/* Why we ask section */}
        {step.whyWeAsk && (
          <div className="mb-6 rounded-2xl border border-[#E8DFBD] bg-white p-6">
            <div className="flex items-center gap-2 mb-4">
              <img src="/lightbulb.svg" alt="" className="w-5 h-5" />
              <h3 className="text-base font-semibold text-[#304031]">{step.whyWeAsk.title}</h3>
            </div>
            {step.whyWeAsk.subtitle && (
              <p className="mb-3 text-sm text-[#304031]">{step.whyWeAsk.subtitle}</p>
            )}
            <ul className="space-y-2 text-sm text-[#304031]">
              {step.whyWeAsk.items.map((item, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#304031] flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Footer text */}
        {step.footerText && (
          <p className="text-sm text-[#304031]/70">{step.footerText}</p>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#fffcef] px-6">
      <div className="mx-auto w-[90vw] py-12 text-[#304031]">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-[#304031]"></div>
            <span className="ml-4 text-base text-[#304031]/70">Loading your information...</span>
          </div>
        ) : (
          <>
            {/* Error message */}
            {error && (
              <div className="mb-6 rounded-lg border border-red-300 bg-red-50 p-4">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {/* Question content */}
            <div className="mb-8">
              {renderQuestion()}
            </div>

            {/* Navigation */}
            <div className="flex gap-4 items-center pt-4">
              {currentStep > 0 && (
                <button
                  onClick={handlePrevious}
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-full border border-[#304031] px-6 py-3 text-base font-semibold text-[#304031] transition hover:bg-[#304031]/5 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  ← Back
                </button>
              )}

              <button
                onClick={handleNext}
                disabled={!canProceedToNext() || saving}
                className={`flex items-center gap-2 rounded-full px-8 py-3 text-base font-semibold transition ${
                  canProceedToNext() && !saving
                    ? 'bg-[#304031] text-[#fef6d6] hover:bg-[#304031]/90'
                    : 'cursor-not-allowed bg-gray-300 text-gray-500'
                }`}
              >
                {saving && (
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#fef6d6] border-t-transparent"></div>
                )}
                {isLastStep ? (saving ? 'Saving...' : 'Save & Continue') : 'Continue'} →
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
